import { History } from 'history';
import React from 'react';
import Link from '~/App/shared/components/Link';
import Auction from '~/App/shared/interfaces/Auction';

/**
 * @deprecated TODO: [KVDBIL-1634] refactor...
 */

import ProcessObject, {
  ConditionClassification
} from '~/App/shared/interfaces/ProcessObject';
import { BaseObjectType } from '~/App/shared/types/BaseObjectType';
import { MainCategory } from '~/App/shared/types/MainCategoryTypes';
import { TranslateFunction } from '~/Locale';
import {
  ConditionInfoRecord,
  ObjectConditionRecord
} from '~/config/generated/graphql';
import {
  getObjectTypeOptions,
  getPathFromMainCategory
} from '~/helpers/filterTranslation';
import { getVehicleTypeTranslation } from '~/helpers/vehicleType';

/**
 * @deprecated TODO: [KVDBIL-1634] this should be moved or removed
 */
export const equipmentExists = (process: ProcessObject) => {
  const hasFormattedEquipment =
    (process?.properties?.formattedEquipment?.length ?? 0) > 0;

  const hasProtocolEquipment =
    (process?.valuation?.endUserTestProtocol?.equipment?.filter(Boolean)
      ?.length ?? 0) > 0;

  return hasFormattedEquipment || hasProtocolEquipment;
};

export const isHeavyEquipmentObject = (processObject: ProcessObject) => {
  return Boolean(processObject?.properties?.heavyEquipment);
};

export const isOffsiteLocation = (processObject: ProcessObject | undefined) => {
  return Boolean(processObject?.offsiteSalesContact);
};

export const getObjectLocationData = (processObject: ProcessObject) => {
  const isOffSite = isOffsiteLocation(processObject);

  if (isOffSite) {
    const data = processObject?.offsiteSalesContact;
    return {
      isOffSite,
      longitude: data?.cityLongitude,
      latitude: data?.cityLatitude,
      city: data?.city,
      name: data?.city
    };
  } else {
    const facility = processObject?.locationInfo?.facility;

    return {
      isOffSite,
      longitude: facility?.longitude,
      latitude: facility?.latitude,
      city: facility?.city,
      name: facility?.name,
      address: facility?.address,
      postCode: facility?.postCode
    };
  }
};

export type BreadcrumbItemKey =
  | 'vehicleType'
  | 'objectType'
  | 'brand'
  | 'familyName'
  | 'headline';

export interface BreadcrumbItem {
  key: BreadcrumbItemKey;
  label: string;
  to?: string;
}

export const getBreadcrumbItems = (
  auction: Auction,
  t: TranslateFunction
): BreadcrumbItem[] => {
  const processObject = auction.processObject;
  const { vehicleType, properties } = processObject;
  const isHeavyEquipment = properties?.heavyEquipment;
  const brand = properties?.brand;
  const familyName = properties?.familyName;
  const modelName = properties?.modelName;
  const encodedBrand = encodeURIComponent(brand);
  const encodedFamilyName = encodeURIComponent(familyName);

  const filterPageUri = getPathFromMainCategory(t, vehicleType);
  const objectTypeOption = getObjectTypeOptions(
    t,
    processObject.vehicleType.toLowerCase() as MainCategory
  ).filter(option => {
    return option.value === processObject.objectType.toLowerCase();
  })[0];

  const items: (BreadcrumbItem | false)[] = [
    {
      key: 'vehicleType',
      label: getVehicleTypeTranslation(vehicleType, t),
      to: filterPageUri
    },
    Boolean(isHeavyEquipment) && {
      key: 'objectType',
      label: objectTypeOption?.label,
      to: `${filterPageUri}?objectTypes=${objectTypeOption?.value}`
    },
    Boolean(brand) && {
      key: 'brand',
      label: brand,
      to: `${filterPageUri}?brand=${encodedBrand}`
    },
    Boolean(Boolean(brand) && Boolean(familyName)) && {
      key: 'familyName',
      label: familyName,
      to: `${filterPageUri}?brand=${encodedBrand}&familyName=${encodedFamilyName}`
    },
    Boolean(Boolean(modelName) || Boolean(auction.previewTitle)) && {
      key: 'headline',
      label: modelName || auction.previewTitle || ''
    }
  ];

  return items.filter((item): item is BreadcrumbItem => item !== false);
};

export const getBreadcrumbs = (auction: Auction, t: TranslateFunction) => {
  return getBreadcrumbItems(auction, t).map(({ label, to }) => ({
    component: to ? <Link to={to}>{label}</Link> : label
  })) as { component: JSX.Element }[];
};

export const getMobileBreadcrumb = (auction: Auction, t: TranslateFunction) => {
  const items = getBreadcrumbItems(auction, t);

  const triggerIndex = Math.max(
    items.findIndex(item => item.key === 'brand'),
    0
  );

  return {
    trigger: items[triggerIndex],
    items: items.slice(triggerIndex + 1)
  };
};

export const getIsEVorPHEV = (processObject: ProcessObject) => {
  const electricType = processObject?.properties?.electricType;
  return electricType === 'Electric' || electricType === 'Plug-in hybrid';
};

export const navigateBasedOnPreviousPage = (
  t: TranslateFunction,
  previousURL: string,
  history: History,
  pathToNavigateTo: string
) => {
  const previousURLHost = previousURL ? new URL(previousURL).host : '';
  const currentURLHost = window.location.host;
  const previousPageIsGallery = previousURL?.includes(t('ROUTES.GALLERY'));

  if (
    previousURLHost === currentURLHost &&
    !previousPageIsGallery &&
    document.referrer !== previousURL // If the link as been opened in a new tab, -1 is not possible
  ) {
    history.go(-1);
  } else {
    history.push(pathToNavigateTo);
  }
};

export function getConditionAnswer(
  conditionInfo: ConditionInfoRecord[],
  grade: number
) {
  return conditionInfo.find(info => Number(info.conditionStep) === grade)
    ?.answer;
}

export function getConditionKey(
  gradeType: 'ENGINE_AND_GEARBOX' | 'BATTERY',
  electricType?: 'Electric' | 'Plug-in hybrid' | 'Electric hybrid' | null
) {
  if (!electricType) return 'DRIVETRAIN';

  const vehicleType =
    electricType === 'Electric'
      ? 'Electric'
      : ['Plug-in hybrid', 'Electric hybrid'].includes(electricType)
        ? 'Hybrid'
        : 'default';

  const gradeTypes = {
    BATTERY: {
      Electric: 'BATTERY_TEST_EV',
      Hybrid: 'BATTERY_TEST_HYBRID',
      default: ''
    },
    ENGINE_AND_GEARBOX: {
      Electric: 'DRIVETRAIN_EV',
      Hybrid: 'DRIVETRAIN_HYBRID',
      default: 'DRIVETRAIN'
    }
  };

  return gradeTypes[gradeType]?.[vehicleType];
}

const LOW_MILEAGE_MAX = 4000;
const MEDIUM_MILEAGE_MAX = 8000;
const HIGH_MILEAGE_MAX = 14000;

export function getMileageGrade(mileage: number) {
  if (mileage <= LOW_MILEAGE_MAX) return 1;
  if (mileage > LOW_MILEAGE_MAX && mileage <= MEDIUM_MILEAGE_MAX) return 2;
  if (mileage > MEDIUM_MILEAGE_MAX && mileage <= HIGH_MILEAGE_MAX) return 3;
  return 4;
}

type TranslatedVehicleTypes = {
  [index in BaseObjectType]: string;
};
export const getTranslatedObjectTypes = (
  t: TranslateFunction
): TranslatedVehicleTypes => ({
  CAR: t('noun.CAR'),
  LIGHT_TRUCK: t('noun.LIGHT_TRUCK'),
  LIGHT_TRAILER: t('noun.LIGHT_TRAILER'),
  LIGHT_TRANSPORT_OTHER: t('noun.LIGHT_TRANSPORT_OTHER'),
  HEAVY_TRUCK: t('noun.HEAVY_TRUCK'),
  HEAVY_TRAILER: t('noun.HEAVY_TRAILER'),
  HEAVY_TRANSPORT_OTHER: t('noun.HEAVY_TRANSPORT_OTHER'),
  CONSTRUCTION_MACHINE: t('noun.CONSTRUCTION_MACHINE'),
  EXCAVATOR: t('noun.EXCAVATOR'),
  DUMPER_HAULER: t('noun.DUMPER_HAULER'),
  LOADING_MACHINE: t('noun.LOADING_MACHINE'),
  MOTORIZED_EQUIPMENT: t('noun.MOTORIZED_EQUIPMENT'),
  COMPACT_TRACTOR: t('noun.COMPACT_TRACTOR'),
  RIDING_MOWER: t('noun.RIDING_MOWER'),
  TRACTOR: t('noun.TRACTOR'),
  MACHINE_OTHER: t('noun.MACHINE_OTHER'),
  TRAILER: t('noun.TRAILER'),
  MC_MOPED: t('noun.MC_MOPED'),
  CAMPER: t('noun.CAMPER'),
  MOBILE_HOME: t('noun.MOBILE_HOME'),
  RECREATIONAL_OTHER: t('noun.RECREATIONAL_OTHER'),

  /**
   * @deprecated should use recreationalObjectTypes in the future
   *  TODO: Removed in the future
   */
  RECREATIONAL_VEHICLE: t('noun.RECREATIONAL_VEHICLE'),
  CARAVAN: t('noun.CARAVAN'),
  BOAT: t('noun.BOAT'),
  OTHER: t('noun.OTHER')
});

export function getGradesAndRemarks(
  properties: ProcessObject['properties'],
  valuation: ProcessObject['valuation']
) {
  return {
    grades: properties?.grades ?? valuation?.endUserTestProtocol?.grades,
    remarks:
      properties?.remarks ?? valuation?.endUserTestProtocol?.remarks ?? {}
  };
}

export function getConditionText(
  condition: ConditionClassification,
  t: TranslateFunction
) {
  switch (condition) {
    case 'CERTIFIED':
      return t('Certified');
    case 'CERTIFIED_PLUS':
      return t('Certified plus');
    case 'REPAIR_OBJECT':
      return t('Repair object');
    case 'TESTED':
      return t('Tested');
    default:
      return '';
  }
}

export function getConditionContentData(
  cmsData: ObjectConditionRecord[],
  conditionClassification: ConditionClassification | null
) {
  return cmsData?.filter(c => {
    return c?.condition === conditionClassification;
  })[0];
}

export function generateBattStatCMSKey(
  battStatValue: string | number | null,
  conditionClassification: ConditionClassification | null
) {
  const isRepairObject = conditionClassification === 'REPAIR_OBJECT';

  return `BATTSTAT_${
    isRepairObject && battStatValue === null ? 'null_REPAIR' : battStatValue
  }`.toUpperCase();
}

export function hasLocationData(processObject?: ProcessObject) {
  return Boolean(
    processObject?.locationInfo?.facility || processObject?.offsiteSalesContact
  );
}
